import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { CheckCircle2, XCircle, AlertCircle, Loader2, Calendar, Clock, Users } from 'lucide-react';

const ReservationActionPage = () => {
    const [searchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    const reservationId = searchParams.get('id');
    const token = searchParams.get('token');
    const action = searchParams.get('action');

    useEffect(() => {
        const processAction = async () => {
            if (!reservationId || !token || !action) {
                setError('Lien invalide ou incomplet');
                setLoading(false);
                return;
            }

            try {
                const API_URL = process.env.REACT_APP_BACKEND_URL;
                const response = await fetch(
                    `${API_URL}/api/reservations/public/action?id=${reservationId}&token=${token}&action=${action}`
                );
                const data = await response.json();
                setResult(data);
            } catch (err) {
                setError('Erreur de connexion. Veuillez réessayer.');
            } finally {
                setLoading(false);
            }
        };

        processAction();
    }, [reservationId, token, action]);

    // Format date
    const formatDate = (dateStr) => {
        if (!dateStr) return '';
        try {
            const date = new Date(dateStr);
            return date.toLocaleDateString('fr-FR', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        } catch {
            return dateStr;
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
                <Card className="w-full max-w-md bg-white/95 backdrop-blur">
                    <CardContent className="p-8 text-center">
                        <Loader2 className="w-12 h-12 mx-auto text-blue-600 animate-spin mb-4" />
                        <p className="text-gray-600">Traitement en cours...</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
                <Card className="w-full max-w-md bg-white/95 backdrop-blur">
                    <CardContent className="p-8 text-center">
                        <AlertCircle className="w-16 h-16 mx-auto text-amber-500 mb-4" />
                        <h1 className="text-xl font-bold text-gray-900 mb-2">Oops!</h1>
                        <p className="text-gray-600">{error}</p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    const isSuccess = result?.success;
    const isConfirm = result?.action === 'confirm';
    const isCancel = result?.action === 'cancel';
    const isAlreadyDone = result?.already_done;

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 flex items-center justify-center p-4">
            <Card className="w-full max-w-md bg-white/95 backdrop-blur overflow-hidden">
                {/* Header */}
                <div className={`p-6 text-center ${
                    isSuccess 
                        ? isConfirm 
                            ? 'bg-gradient-to-r from-green-500 to-emerald-600' 
                            : 'bg-gradient-to-r from-orange-500 to-red-500'
                        : 'bg-gradient-to-r from-gray-500 to-gray-600'
                }`}>
                    <h2 className="text-white text-sm font-medium mb-2 opacity-90">
                        ALDRIN'S Restaurant
                    </h2>
                    {isSuccess ? (
                        isConfirm ? (
                            <CheckCircle2 className="w-16 h-16 mx-auto text-white mb-2" />
                        ) : (
                            <XCircle className="w-16 h-16 mx-auto text-white mb-2" />
                        )
                    ) : (
                        <AlertCircle className="w-16 h-16 mx-auto text-white mb-2" />
                    )}
                    <h1 className="text-2xl font-bold text-white">
                        {isSuccess 
                            ? isConfirm 
                                ? (isAlreadyDone ? 'Déjà confirmé' : 'Venue confirmée !') 
                                : (isAlreadyDone ? 'Déjà annulé' : 'Réservation annulée')
                            : 'Action impossible'
                        }
                    </h1>
                </div>

                <CardContent className="p-6">
                    {/* Message */}
                    <p className={`text-center mb-6 ${
                        isSuccess ? 'text-gray-700' : 'text-red-600'
                    }`}>
                        {result?.message}
                    </p>

                    {/* Reservation details */}
                    {result?.reservation && (
                        <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                            <h3 className="font-semibold text-gray-900 text-sm uppercase tracking-wide mb-3">
                                Détails de la réservation
                            </h3>
                            
                            {result.reservation.customer_name && (
                                <div className="flex items-center gap-3 text-gray-700">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
                                        <Users className="w-4 h-4 text-blue-600" />
                                    </div>
                                    <span className="font-medium">{result.reservation.customer_name}</span>
                                </div>
                            )}
                            
                            {result.reservation.date && (
                                <div className="flex items-center gap-3 text-gray-700">
                                    <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                                        <Calendar className="w-4 h-4 text-green-600" />
                                    </div>
                                    <span>{formatDate(result.reservation.date)}</span>
                                </div>
                            )}
                            
                            {result.reservation.time && (
                                <div className="flex items-center gap-3 text-gray-700">
                                    <div className="w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center">
                                        <Clock className="w-4 h-4 text-purple-600" />
                                    </div>
                                    <span>{result.reservation.time}</span>
                                </div>
                            )}
                            
                            {result.reservation.party_size && (
                                <div className="flex items-center gap-3 text-gray-700">
                                    <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                                        <Users className="w-4 h-4 text-amber-600" />
                                    </div>
                                    <span>{result.reservation.party_size} personne{result.reservation.party_size > 1 ? 's' : ''}</span>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Contact info */}
                    <div className="mt-6 pt-6 border-t border-gray-200 text-center">
                        <p className="text-sm text-gray-500">
                            Des questions ? Contactez-nous
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                            📞 Téléphone du restaurant
                        </p>
                    </div>

                    {/* Close button */}
                    <Button 
                        className="w-full mt-6"
                        variant={isSuccess && isConfirm ? "default" : "outline"}
                        onClick={() => window.close()}
                    >
                        Fermer cette page
                    </Button>
                </CardContent>
            </Card>
        </div>
    );
};

export default ReservationActionPage;
